import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { EditProfileForm } from '@/components/EditProfileForm';
import { HomeLink } from '@/components/HomeLink';
import { useToast } from '@/hooks/use-toast';
import { Loader2, AlertCircle, WifiOff, User, Star, Lock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogDescription } from "@/components/ui/dialog";
import { ChangePasswordForm } from "@/components/auth/ChangePasswordForm";

const Profile = () => {
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Authentication required",
        description: "Please sign in to view your profile",
        variant: "destructive",
      });
      navigate('/auth');
      return false;
    }
    return true;
  };

  const getProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) return;

      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session!.user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        if (error.code === 'PGRST116' || error.message.includes('fetch')) {
          setError('Unable to connect to the server. Please check your internet connection and try again.');
        } else {
          setError(error.message);
        }
        return;
      }

      if (!data) {
        setError("Profile not found");
        return;
      }

      setProfile(data);
    } catch (error: any) {
      console.error('Profile loading error:', error);
      if (error.message?.includes('refresh_token_not_found')) {
        toast({
          title: "Session expired",
          description: "Please sign in again",
          variant: "destructive",
        });
        navigate('/auth');
        return;
      }
      setError('Unable to connect to the server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  const handleRetry = () => {
    getProfile();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gamePurple to-gameSlate p-8 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-gameGold" />
          <p className="text-gameGold">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gamePurple to-gameSlate p-8">
      <HomeLink />
      <div className="max-w-2xl mx-auto mt-16">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <User className="h-6 w-6 text-gameGold" />
            <h1 className="text-3xl font-bold text-gameGold">Profile</h1>
          </div>
          
          {profile && (
            <Button
              onClick={() => setIsChangingPassword(true)}
              variant="outline"
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
            >
              <Lock className="h-4 w-4 mr-2" />
              Change Password
            </Button>
          )}
        </div>
        
        {error ? (
          <Alert variant="destructive" className="mb-8">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Connection Error</AlertTitle>
            <AlertDescription className="mt-2">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <WifiOff className="h-4 w-4" />
                  <span>{error}</span>
                </div>
                <Button 
                  onClick={handleRetry}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  Try Again
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : profile ? (
          <>
            <Card className="mb-8 bg-white/10 border-white/20">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-gameGold" />
                  <div className="text-2xl font-bold text-gameGold">{profile.points}</div>
                </div>
                <CardDescription className="text-gray-300">
                  Total points earned
                </CardDescription>
              </CardContent>
            </Card>
            <EditProfileForm
              profile={profile}
              userId={profile.id}
              onSuccess={getProfile}
              onCancel={() => navigate('/')}
            />
          </>
        ) : (
          <div className="text-center text-white">
            <p>Unable to load profile data. Please try again later.</p>
          </div>
        )}
      </div>

      {/* Password change dialog */}
      <Dialog open={isChangingPassword} onOpenChange={setIsChangingPassword}>
        <DialogContent className="bg-gameSlate text-white border-white/20">
          <DialogHeader>
            <DialogTitle className="text-gameGold">Change Your Password</DialogTitle>
            <DialogDescription className="text-gray-300">
              Update your account password securely.
            </DialogDescription>
          </DialogHeader>
          <ChangePasswordForm onCancel={() => setIsChangingPassword(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
